import { Refine, ResourceProps } from '@refinedev/core';
import { App as AntdApp, Button, ConfigProvider, Spin, theme } from 'antd';
import {
    Outlet,
    Routes,
    Route,
    Navigate,
    useLocation,
    BrowserRouter,
} from 'react-router-dom';
import { PublicRoute } from './components/public-route';
import { ProtectedRoute } from './components/protected-route';
import { AppLayout } from './components/app-layout';
import routerProvider, { NavigateToResource } from '@refinedev/react-router-v6';
import { dataProvider } from './provider/dataProvider';
import { notificationProvider } from './provider/notificationProvider';
import { api } from './utils/api';
import { useTranslation } from 'react-i18next';
import { Suspense, useEffect, useState } from 'react';
import routerProviderData from './config/route';
import { usePageName } from './hooks/usePageName';
import { authProvider } from './authProvider';

import { Login } from './pages/login';
import { BetterHelmet } from '@limeyfy/react-seo';
import { TusClientProvider } from 'use-tus';
import { NotificationContextProvider } from './context/NotificationContext';

import { useUserData } from './hooks/useUserData';
import { useLocalizedResources } from './hooks/useLocalizedResources';

import uk_UA from 'antd/es/locale/uk_UA';
import en_US from 'antd/es/locale/en_US';
import 'antd/dist/reset.css';

import { roleAccess, rolePriority } from './config/roleAccess';
import { VisionProvider } from './context/vision';
import { PublicLayout } from './components/layout';
import { SidebarAuthorization } from './components/sidebar';
import { AccessibilityProvider } from './contexts/AccessibilityContext';

const { routes } = routerProviderData;

function App() {
    const { t, i18n } = useTranslation();
    const { user } = useUserData();
    const resources = useLocalizedResources();
    const [newResources, setNewResources] = useState<
        ResourceProps[] | undefined
    >([]);

    const i18nProvider = {
        translate: (key: string, params: object) => t(key, params),
        changeLocale: (lang: string) => i18n.changeLanguage(lang),
        getLocale: () => i18n.language,
    };

    const { pathname } = useLocation();
    const { pageName } = usePageName(pathname, t);

    const title = `${t('labels.unified_office_of_the_applicant')}: ${pageName}`;
    const locale = i18nProvider.getLocale();

    const routerProviderConfig = {
        ...routerProvider,
        BrowserRouter,
        Routes,
        Route,
        Outlet,
        NavigateToResource,
    };

    useEffect(() => {
        const getHighestPriorityRole = (roles: string[]): string | null => {
            let highest: string | null = null;
            let priority = -Infinity;
            for (const role of roles) {
                const p = rolePriority[role] ?? 0;
                if (p > priority) {
                    highest = role;
                    priority = p;
                }
            }
            return highest;
        };

        const filterResourcesByRole = (resources: ResourceProps[]) => {
            const topRole = getHighestPriorityRole(user?.roles ?? []);
            // @ts-ignore
            const allowed = topRole ? roleAccess[topRole] ?? [] : [];
            return resources.filter(res => allowed.includes(res.name));
        };

        if (user) {
            const filtered = filterResourcesByRole(resources);
            setNewResources(filtered);
        } else {
            setNewResources(resources); // гостьовий доступ
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname, user, t]);

    // @ts-ignore
    return (
        <TusClientProvider>
            <BetterHelmet>
                <title>{title.trim()}</title>
                <meta name="description" content={pageName} />
            </BetterHelmet>

            <NotificationContextProvider>
                <ConfigProvider
                    locale={locale === 'uk' ? uk_UA : en_US}
                    theme={{
                        components: {
                            Button: {
                                borderRadius: 25,
                                // defaultHoverBg: '#f00000',
                            },
                            Typography: {
                                // colorTextHeading: '#1890ff',
                                colorBgBase: 'yellow',
                                colorBgTextHover: 'blue',
                                colorBgSolid: 'gold',
                            },
                            Dropdown: {
                                // colorBgElevated: '#fff',
                                // colorText: '#000',
                                borderRadius: 8,
                                padding: 4,
                                boxShadowSecondary:
                                    '0px 8px 20px rgba(0,0,0,0.25)',
                                colorTextBase: 'white',
                            },
                            Select: {
                                optionActiveBg: '#E7EEF3',
                                optionSelectedBg: '#E7EEF3',
                                optionSelectedColor: '#000000',
                                optionSelectedFontWeight: 600,
                            },
                            Menu: {},
                        },
                        token: {
                            colorPrimary: '#000000',
                            fontFamily: 'e-Ukraine, sans-serif',
                        },
                    }}
                >
                    <AccessibilityProvider>
                        <VisionProvider>
                            <Refine
                            dataProvider={dataProvider(api)}
                            authProvider={authProvider}
                            routerProvider={routerProviderConfig}
                            notificationProvider={notificationProvider}
                            resources={newResources ?? []}
                            options={{
                                syncWithLocation: true,
                                warnWhenUnsavedChanges: true,
                            }}
                            i18nProvider={i18nProvider}
                        >
                            <AntdApp>
                                <Suspense
                                    fallback={
                                        <Spin
                                            size="large"
                                            style={{
                                                display: 'block',
                                                margin: 'auto',
                                                marginTop: '20vh',
                                            }}
                                        />
                                    }
                                >
                                    <Routes>
                                        <Route
                                            path="/"
                                            element={
                                                <ProtectedRoute>
                                                    <NavigateToResource />
                                                </ProtectedRoute>
                                            }
                                        />

                                        <Route
                                            path="/login"
                                            element={
                                                <PublicRoute>
                                                    <PublicLayout
                                                        sidebar={
                                                            <SidebarAuthorization />
                                                        }
                                                    >
                                                        <Login />
                                                    </PublicLayout>
                                                </PublicRoute>
                                            }
                                        />

                                        <Route
                                            element={
                                                <ProtectedRoute>
                                                    <AppLayout />
                                                </ProtectedRoute>
                                            }
                                        >
                                            {routes.map(route => {
                                                const RouteElement =
                                                    route.isPublic ? (
                                                        route.element
                                                    ) : (
                                                        <ProtectedRoute>
                                                            {route.element}
                                                        </ProtectedRoute>
                                                    );

                                                return (
                                                    <Route
                                                        key={route.path}
                                                        path={route.path}
                                                        element={RouteElement}
                                                    />
                                                );
                                            })}
                                        </Route>

                                        <Route
                                            path="*"
                                            element={<Navigate to="/" />}
                                        />
                                    </Routes>
                                </Suspense>
                            </AntdApp>
                            </Refine>
                        </VisionProvider>
                    </AccessibilityProvider>
                </ConfigProvider>
            </NotificationContextProvider>
        </TusClientProvider>
    );
}

export default App;
